import type { Course, CourseCalendarDateCustom } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { filter, find, includes, isNil, map, max, min } from "lodash-es"

/**
 * Use this to find if a given date is on/off, in/out of the school year, has
 * date annotations, etc. Each world passes its own phantom finder: the Ember component injects
 * `phantomCardStacks.findCardStackIdForDate`, the React one a `phantom-lesson.ts` store scan.
 */
export function calculateCourseDateCombined(
  dateString: string,
  course: Course,
  planbook: Planbook,
  rotationCalendar: RotationCalendar,
  phantomCardStackIdFinder: (courseId: string, dateString: string) => string | null,
): CourseDateCombined {
  let rotationDate = find(
    rotationCalendar.attributes.dates,
    (date) => date.attributes.dateString === dateString,
  ) || { attributes: { semesterId: null, rotationId: null } }

  let courseDateCustom =
    find(
      filter(course.attributes.calendar.dates, (date) => date.type === "course-date-custom"),
      (date: CourseCalendarDateCustom) => date.attributes.dateString === dateString,
    ) || null

  let courseDateDefault = find(
    filter(course.attributes.calendar.dates, (date) => date.type === "course-date-default"),
    (date) => {
      return (
        date.attributes.semesterId === rotationDate.attributes.semesterId &&
        date.attributes.rotationId === rotationDate.attributes.rotationId
      )
    },
  )

  let firstDay = min(map(rotationCalendar.attributes.semesters, "startDate"))
  let lastDay = max(map(rotationCalendar.attributes.semesters, "endDate"))
  if (firstDay === undefined || lastDay === undefined) throw Error("No start or end days")

  let planbookDateDefaultIsOff = includes(
    planbook.attributes.calendar.datesOff,
    `${rotationDate.attributes.semesterId}:${rotationDate.attributes.rotationId}`,
  )
  let planbookDateCustomIsOff = includes(planbook.attributes.calendar.datesOff, dateString)
  let courseDateDefaultIsOff = includes(
    course.attributes.calendar.datesOff,
    `${rotationDate.attributes.semesterId}:${rotationDate.attributes.rotationId}`,
  )
  let courseDateCustomIsOff = includes(course.attributes.calendar.datesOff, dateString)

  let planbookIsOff = planbookDateCustomIsOff ? planbookDateCustomIsOff : planbookDateDefaultIsOff
  let courseIsOff = courseDateCustomIsOff ? courseDateCustomIsOff : courseDateDefaultIsOff
  let courseDateForcedOn = courseDateCustom ? courseDateCustom.attributes.isForcedOn : false

  let isOff = planbookIsOff || (courseIsOff && !courseDateForcedOn)

  let visibleCourseDateAnnotations = filter(
    course.attributes.calendar.dateAnnotations,
    (annotation) => {
      return (
        annotation.type === "date-annotation-course-custom-single" &&
        annotation.attributes.dateString === dateString &&
        annotation.attributes.showAnnotation === true
      )
    },
  )

  let cardStackId = courseDateCustom ? courseDateCustom.attributes.cardStackId : null
  let hasCardStackId = cardStackId !== null
  let cardStackTemplateId = courseDateDefault ? courseDateDefault.attributes.cardStackId : null

  const timeCustom = courseDateCustom ? courseDateCustom.attributes.time : null
  const timeDefault = courseDateDefault ? courseDateDefault.attributes.time : null

  let phantomCardStackId =
    cardStackId === null ? phantomCardStackIdFinder(course.id, dateString) : null

  let hasLesson = isNil(courseDateCustom?.attributes.cardStackId) !== true

  return {
    cardStackId: cardStackId,
    cardStackTemplateId: cardStackTemplateId,
    danglingLessonType: isOff && hasLesson ? (planbookIsOff ? "planbook" : "course") : null,
    dateString: dateString,
    hasLesson: hasLesson,
    hasNoTemplate: cardStackTemplateId === null,
    hasVisibleAnnotations: visibleCourseDateAnnotations.length > 0,
    hideFromCalendar: isOff && hasCardStackId !== true && visibleCourseDateAnnotations.length === 0,
    isAfterSchoolYear: dateString > lastDay,
    isBeforeSchoolYear: dateString < firstDay,
    isForcedOn: courseDateForcedOn,
    isInsideSchoolYear: dateString >= firstDay && dateString <= lastDay,
    isNewLesson: cardStackId === null,
    isOff: isOff,
    isOffByDefault: courseDateDefaultIsOff,
    phantomCardStackId: phantomCardStackId,
    showLesson: isOff === false || (isOff === true && hasLesson),
    timeCustom,
    timeDefault,
    unitEnd: courseDateCustom ? courseDateCustom.attributes.unitEnd : [],
    unitIds: courseDateCustom ? courseDateCustom.attributes.unitIds : [],
    unitStart: courseDateCustom ? courseDateCustom.attributes.unitStart : [],
    visibleAnnotations: visibleCourseDateAnnotations,
  }
}
